/**
 * ExecutionLogPanel — streaming activity log for ad generation jobs.
 *
 * Shows:
 *   - Insights tab: human-readable insight_log entries (append-only)
 *   - Steps tab: structured step lifecycle (StepLogEntry)
 *   - Errors tab: only visible when failures exist — surfaces errors immediately
 *   - Raw tab (dev only): full execution_log JSON
 *
 * Accepts an optional `filterPhase` to scope the Insights and Steps tabs to a
 * single pipeline phase (for the linked phase-timeline debug view).
 */
import { useEffect, useRef, useState } from 'react';
import {
  Terminal,
  ChevronDown,
  ChevronUp,
  Radio,
  Layers,
  FileText,
  Code,
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
  SkipForward,
  AlertTriangle,
  X,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { ExecutionLog, InsightLogEntry, StepLogEntry } from '@/services/api';

// ---------------------------------------------------------------------------
// Phase color / label maps — exported so AdGenerationPage can import them
// ---------------------------------------------------------------------------

export const PHASE_COLORS: Record<string, string> = {
  resolve_client: 'bg-slate-100 text-slate-700 border-slate-200',
  load_brief: 'bg-purple-50 text-purple-700 border-purple-200',
  load_brand: 'bg-purple-50 text-purple-700 border-purple-200',
  fetch_products: 'bg-blue-50 text-blue-700 border-blue-200',
  fetch_personas: 'bg-blue-50 text-blue-700 border-blue-200',
  load_graph: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  compute_affinities: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  seed_scenes: 'bg-amber-50 text-amber-700 border-amber-200',
  select_scenarios: 'bg-amber-50 text-amber-700 border-amber-200',
  interpret_brief: 'bg-orange-50 text-orange-700 border-orange-200',
  generate_briefs: 'bg-teal-50 text-teal-700 border-teal-200',
  generate_images: 'bg-teal-50 text-teal-700 border-teal-200',
  review_ads: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  refine_ads: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  generate_lp_briefs: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  package: 'bg-green-50 text-green-700 border-green-200',
  unknown: 'bg-slate-50 text-slate-500 border-slate-200',
};

export const PHASE_LABELS: Record<string, string> = {
  resolve_client: 'Resolve Client',
  load_brief: 'Load Brief',
  load_brand: 'Load Brand',
  fetch_products: 'Fetch Products',
  fetch_personas: 'Fetch Personas',
  load_graph: 'Load Graph',
  compute_affinities: 'Compute Affinities',
  seed_scenes: 'Seed Scenes',
  select_scenarios: 'Select Scenarios',
  interpret_brief: 'Interpret Brief',
  generate_briefs: 'Generate Briefs',
  generate_images: 'Generate Images',
  review_ads: 'Review Ads',
  refine_ads: 'Refine Ads',
  generate_lp_briefs: 'LP Briefs',
  package: 'Package',
};

export function phaseLabel(phase?: string): string {
  if (!phase) return '-';
  return PHASE_LABELS[phase] ?? phase.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

export function phaseColor(phase: string): string {
  return PHASE_COLORS[phase] ?? PHASE_COLORS.unknown;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

function stepStatusIcon(status: string) {
  switch (status) {
    case 'completed':
      return <CheckCircle2 size={12} className="text-emerald-500 flex-shrink-0" />;
    case 'failed':
      return <XCircle size={12} className="text-rose-500 flex-shrink-0" />;
    case 'running':
      return <Loader2 size={12} className="text-brand-teal animate-spin flex-shrink-0" />;
    case 'skipped':
      return <SkipForward size={12} className="text-slate-400 flex-shrink-0" />;
    default:
      return <Clock size={12} className="text-slate-300 flex-shrink-0" />;
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

export function PhaseBadge({ phase }: { phase: string }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold rounded border whitespace-nowrap flex-shrink-0',
        phaseColor(phase),
      )}
    >
      {phaseLabel(phase)}
    </span>
  );
}

function InsightRow({
  entry,
  isLatest,
  isGenerating,
}: {
  entry: InsightLogEntry;
  isLatest: boolean;
  isGenerating: boolean;
}) {
  return (
    <div
      className={clsx(
        'flex items-start gap-3 py-2 px-3 rounded-lg transition-colors',
        isLatest && isGenerating && 'bg-brand-teal/5 border border-brand-teal/15',
      )}
    >
      <span className="text-[10px] font-mono text-slate-400 shrink-0 w-20 pt-0.5">
        {formatTimestamp(entry.timestamp)}
      </span>
      <PhaseBadge phase={entry.phase} />
      <span className="text-xs text-slate-700 flex-1 leading-relaxed">{entry.insight}</span>
      {entry.progress > 0 && (
        <span className="text-[10px] font-mono text-slate-400 shrink-0">
          {Math.round(entry.progress)}%
        </span>
      )}
    </div>
  );
}

function StepRow({ step }: { step: StepLogEntry }) {
  const duration =
    step.duration_seconds != null ? `${step.duration_seconds.toFixed(1)}s` : null;
  const itemsInfo =
    step.items_total > 0 ? `${step.items_completed}/${step.items_total}` : null;

  return (
    <div className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-slate-50 transition-colors">
      {stepStatusIcon(step.status)}
      <span className="text-xs font-medium text-slate-700 w-40 shrink-0">
        {phaseLabel(step.step_name)}
      </span>
      <span
        className={clsx(
          'text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border',
          step.status === 'completed' && 'bg-emerald-50 text-emerald-700 border-emerald-200',
          step.status === 'running' && 'bg-brand-teal/10 text-brand-teal border-brand-teal/20',
          step.status === 'failed' && 'bg-rose-50 text-rose-700 border-rose-200',
          step.status === 'skipped' && 'bg-slate-100 text-slate-500 border-slate-200',
          step.status === 'pending' && 'bg-slate-50 text-slate-400 border-slate-200',
        )}
      >
        {step.status}
      </span>
      {itemsInfo && (
        <span className="text-[10px] text-slate-500 font-mono">{itemsInfo} items</span>
      )}
      {duration && (
        <span className="text-[10px] text-slate-400 font-mono ml-auto">{duration}</span>
      )}
      {step.message && (
        <span className="text-[10px] text-slate-400 truncate max-w-[200px]">{step.message}</span>
      )}
      {step.error && (
        <div className="flex items-center gap-1 text-[10px] text-rose-600">
          <AlertTriangle size={10} />
          <span className="truncate max-w-[200px]">{step.error}</span>
        </div>
      )}
    </div>
  );
}

function ErrorRow({ step }: { step: StepLogEntry }) {
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
      <div className="flex items-center gap-2 mb-1.5">
        <XCircle size={12} className="text-rose-500 flex-shrink-0" />
        <span className="text-xs font-bold text-rose-800">{phaseLabel(step.step_name)}</span>
        {step.duration_seconds != null && (
          <span className="text-[10px] text-rose-400 font-mono ml-auto">
            {step.duration_seconds.toFixed(1)}s
          </span>
        )}
      </div>
      {step.error && (
        <p className="text-xs text-rose-700 leading-relaxed font-mono whitespace-pre-wrap break-all">
          {step.error}
        </p>
      )}
      {step.message && !step.error && (
        <p className="text-xs text-rose-600 leading-relaxed">{step.message}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

type TabId = 'insights' | 'steps' | 'errors' | 'raw';

interface ExecutionLogPanelProps {
  executionLog?: ExecutionLog;
  status: string;
  currentPhase?: string;
  defaultOpen?: boolean;
  /** Always render expanded — no collapsible chrome */
  alwaysOpen?: boolean;
  /** When set, filter Insights and Steps to this phase only */
  filterPhase?: string | null;
  /** Called when the user clears the phase filter chip */
  onClearFilter?: () => void;
}

export function ExecutionLogPanel({
  executionLog,
  status,
  currentPhase,
  defaultOpen = false,
  alwaysOpen = false,
  filterPhase,
  onClearFilter,
}: ExecutionLogPanelProps) {
  const [open, setOpen] = useState(alwaysOpen || defaultOpen);
  const [activeTab, setActiveTab] = useState<TabId>('insights');
  const scrollRef = useRef<HTMLDivElement>(null);

  const isGenerating = status === 'running' || status === 'pending';
  const allInsights = executionLog?.insight_log ?? [];
  const allSteps = executionLog?.steps ?? [];
  const isDev = import.meta.env.DEV;

  // Apply phase filter
  const insightLog = filterPhase
    ? allInsights.filter((e) => e.phase === filterPhase)
    : allInsights;
  const steps = filterPhase
    ? allSteps.filter((s) => s.step_name === filterPhase)
    : allSteps;

  // Error entries: all failed steps (unfiltered — errors are always global)
  const errorSteps = allSteps.filter((s) => s.status === 'failed' || !!s.error);
  const hasErrors = errorSteps.length > 0;

  // Auto-expand when a job starts generating
  useEffect(() => {
    if (!alwaysOpen && isGenerating && !open) {
      setOpen(true);
    }
  }, [isGenerating]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-switch to errors tab when errors appear
  useEffect(() => {
    if (hasErrors && activeTab !== 'errors') {
      setActiveTab('errors');
    }
  }, [hasErrors]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll insights to bottom while generating
  useEffect(() => {
    if (isGenerating && activeTab === 'insights' && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [insightLog.length, isGenerating, activeTab]);

  const tabs = [
    { id: 'insights' as TabId, icon: FileText, label: 'Insights', count: insightLog.length },
    { id: 'steps' as TabId, icon: Layers, label: 'Steps', count: steps.length },
    ...(hasErrors ? [{ id: 'errors' as TabId, icon: AlertTriangle, label: 'Errors', count: errorSteps.length }] : []),
    ...(isDev ? [{ id: 'raw' as TabId, icon: Code, label: 'Raw', count: undefined }] : []),
  ] as Array<{ id: TabId; icon: React.ElementType; label: string; count?: number }>;

  const body = (
    <>
      {/* Filter chip */}
      {filterPhase && (
        <div className="flex items-center gap-2 px-4 pt-3 pb-0">
          <span className="text-[10px] text-slate-400 uppercase font-bold">Filtered by</span>
          <span
            className={clsx(
              'inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded border',
              phaseColor(filterPhase),
            )}
          >
            {phaseLabel(filterPhase)}
            {onClearFilter && (
              <button
                onClick={onClearFilter}
                className="ml-0.5 opacity-60 hover:opacity-100 transition-opacity"
                aria-label="Clear filter"
              >
                <X size={9} />
              </button>
            )}
          </span>
          {onClearFilter && (
            <button
              onClick={onClearFilter}
              className="text-[10px] text-slate-400 hover:text-slate-600 transition-colors ml-auto"
            >
              Show all phases
            </button>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 px-4 border-b border-slate-100 bg-slate-50/50 mt-1">
        {tabs.map(({ id, icon: Icon, label, count }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors',
              activeTab === id
                ? 'text-navy-900 border-brand-teal'
                : 'text-slate-500 border-transparent hover:text-slate-700',
            )}
          >
            <Icon
              size={12}
              className={clsx(id === 'errors' && activeTab !== 'errors' && 'text-rose-400')}
            />
            {label}
            {count != null && count > 0 && (
              <span
                className={clsx(
                  'text-[10px] px-1.5 rounded-full',
                  id === 'errors'
                    ? 'bg-rose-100 text-rose-600'
                    : 'bg-slate-200 text-slate-600',
                )}
              >
                {count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div
        ref={scrollRef}
        className={clsx(
          'overflow-y-auto px-2 py-2',
          alwaysOpen ? 'max-h-[480px]' : 'max-h-80',
        )}
        style={{ scrollBehavior: 'smooth' }}
      >
        {activeTab === 'insights' && (
          <>
            {insightLog.length === 0 ? (
              <div className="py-10 text-center text-slate-400 text-xs">
                {filterPhase
                  ? `No insights recorded for ${phaseLabel(filterPhase)}.`
                  : isGenerating
                    ? 'Waiting for pipeline events...'
                    : 'No insight events recorded for this job.'}
              </div>
            ) : (
              <div className="space-y-0.5">
                {insightLog.map((entry, i) => (
                  <InsightRow
                    key={i}
                    entry={entry}
                    isLatest={i === insightLog.length - 1}
                    isGenerating={isGenerating}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'steps' && (
          <>
            {steps.length === 0 ? (
              <div className="py-10 text-center text-slate-400 text-xs">
                {filterPhase
                  ? `No step data for ${phaseLabel(filterPhase)}.`
                  : 'No step data available.'}
              </div>
            ) : (
              <div className="space-y-0.5">
                {steps.map((step) => (
                  <StepRow key={step.step_name} step={step} />
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'errors' && (
          <>
            {errorSteps.length === 0 ? (
              <div className="py-10 text-center text-slate-400 text-xs">No errors recorded.</div>
            ) : (
              <div className="space-y-2 p-1">
                {errorSteps.map((step) => (
                  <ErrorRow key={step.step_name} step={step} />
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'raw' && isDev && (
          <pre className="text-[10px] text-slate-600 font-mono whitespace-pre-wrap break-all p-2">
            {JSON.stringify(executionLog, null, 2)}
          </pre>
        )}
      </div>

      {/* Footer */}
      <div className="px-5 py-2.5 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
        <span className="text-[10px] text-slate-400">
          {allInsights.length} events · {allSteps.filter((s) => s.status === 'completed').length}/
          {allSteps.length} steps complete
          {filterPhase && (
            <span className="ml-1 text-slate-300">
              · {insightLog.length} shown
            </span>
          )}
        </span>
        {isGenerating && (
          <span className="flex items-center gap-1.5 text-[10px] text-brand-teal font-medium">
            <Radio size={10} className="animate-pulse" />
            Streaming updates...
          </span>
        )}
      </div>
    </>
  );

  if (alwaysOpen) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Always-open header (no toggle) */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-navy-900/5 flex items-center justify-center border border-slate-200">
              <Terminal size={13} className="text-slate-600" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-navy-900">Execution Log</span>
              {allInsights.length > 0 && (
                <span className="text-[10px] font-mono bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200">
                  {allInsights.length} events
                </span>
              )}
              {hasErrors && (
                <span className="flex items-center gap-1 text-[10px] font-medium text-rose-600">
                  <AlertTriangle size={10} />
                  {errorSteps.length} {errorSteps.length === 1 ? 'error' : 'errors'}
                </span>
              )}
              {isGenerating && (
                <span className="flex items-center gap-1.5 text-[10px] font-medium text-brand-teal">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-teal opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-teal" />
                  </span>
                  LIVE
                </span>
              )}
            </div>
          </div>
          {currentPhase && isGenerating && (
            <PhaseBadge phase={currentPhase} />
          )}
        </div>
        {body}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Collapsible header */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-navy-900/5 flex items-center justify-center border border-slate-200">
            <Terminal size={14} className="text-slate-600" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-navy-900">Execution Log</span>
            {allInsights.length > 0 && (
              <span className="text-[10px] font-mono bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200">
                {allInsights.length} events
              </span>
            )}
            {hasErrors && (
              <span className="flex items-center gap-1 text-[10px] font-medium text-rose-600">
                <AlertTriangle size={10} />
                {errorSteps.length} {errorSteps.length === 1 ? 'error' : 'errors'}
              </span>
            )}
            {isGenerating && (
              <span className="flex items-center gap-1.5 text-[10px] font-medium text-brand-teal">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-teal opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-teal" />
                </span>
                LIVE
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {currentPhase && isGenerating && (
            <PhaseBadge phase={currentPhase} />
          )}
          {open ? (
            <ChevronUp size={14} className="text-slate-400" />
          ) : (
            <ChevronDown size={14} className="text-slate-400" />
          )}
        </div>
      </button>

      {open && body}
    </div>
  );
}
